import type { LocationLevelType } from "api-types/locations/locationLevelTypes";
import type {
  ClientLocationCreationResponse,
  ClientLocationCreationWithMigratedChildrenResponse,
  ClientLocationHydrated,
  ClientLocationWithLevel,
  ClientMigratedChildrenPreviewResponse,
} from "api-types/locations/locationsResponses";
import type { LocationStudioDocSetSchema } from "api-types/locationStudioDocs/locationStudioDocsRequests";
import type { LocationWorkbookSetSchema } from "api-types/locationWorkbooks/locationWorkbooksRequests";
import { WEB_API_BASE_PATH } from "common/constants";
import type { UserNotificationType } from "common/types/userNotification";
import type { FetchErrorResponse } from "common/utils/fetchHelpers";
import { handleFetchError } from "common/utils/fetchHelpers";
import type { LocationUpdateFields } from "lib/components/builders/communities/modals/CommunityDetailsForm";
import { getBuilderLocationStudioDocsURL, getBuilderLocationWorkbookURL } from "lib/endpointHelpers";
import { deleteWithTimeout, getWithTimeout, postWithTimeout, putWithTimeout } from "lib/utils/fetchAuthHelpers";
import type { StorageResponse } from "./types";

const LOCATION_MIGRATION_TIMEOUT = 60 * 1000; // 60 seconds

function baseUrl(builderId: string): string {
  return `${WEB_API_BASE_PATH}/builders/${builderId}/locations-internal`;
}

export async function getLocationWithAncestors(
  builderId: string,
  locationId: string,
): Promise<[ClientLocationWithLevel[], null] | [null, FetchErrorResponse]> {
  try {
    const res = await getWithTimeout(`${baseUrl(builderId)}/${locationId}/parents`);
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function postLocation({
  builderId,
  masterPlanIds,
  locationPlanIds,
  location,
}: {
  builderId: string;
  masterPlanIds?: string[];
  locationPlanIds?: string[];
  location: LocationUpdateFields;
}): Promise<[ClientLocationCreationResponse, null] | [null, FetchErrorResponse]> {
  try {
    const res = await postWithTimeout(
      baseUrl(builderId),
      {
        masterPlanIds,
        locationPlanIds,
        location,
      },
      20 * 1000,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function putLocation(
  builderId: string,
  locationId: string,
  fields: Partial<LocationUpdateFields>,
): StorageResponse<ClientLocationHydrated, null> {
  try {
    const res = await putWithTimeout(`${baseUrl(builderId)}/${locationId}`, { fields });

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function deleteCommunity(builderId: string, locationId: string): Promise<UserNotificationType | null> {
  try {
    const res = await deleteWithTimeout(`${baseUrl(builderId)}/${locationId}`);
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return null;
  } catch (e) {
    return await handleFetchError(e);
  }
}

export async function postPreviewMigratedChildren({
  builderId,
  locationIdsToMigrate,
  parentLocationLevel,
}: {
  builderId: string;
  locationIdsToMigrate: string[];
  parentLocationLevel: LocationLevelType;
}): Promise<[ClientMigratedChildrenPreviewResponse, null] | [null, FetchErrorResponse]> {
  try {
    const res = await postWithTimeout(`${baseUrl(builderId)}/migrate-locations/preview`, {
      locationIdsToMigrate,
      parentLocationLevel,
    });
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function setLocationStudioDoc({
  builderId,
  locationId,
  body,
}: {
  builderId: string;
  locationId: string;
  body: LocationStudioDocSetSchema;
}): Promise<UserNotificationType | null> {
  try {
    const response = await postWithTimeout(getBuilderLocationStudioDocsURL({ builderId, locationId }), body);

    if (!response.ok) {
      throw response;
    }

    return null;
  } catch (error) {
    console.error(error);
    return { messageType: "error", message: "Failed to save studio doc. Please try again." };
  }
}

export async function removeLocationStudioDoc({
  builderId,
  locationId,
}: {
  builderId: string;
  locationId: string;
}): Promise<UserNotificationType | null> {
  try {
    const response = await deleteWithTimeout(getBuilderLocationStudioDocsURL({ builderId, locationId }));

    if (!response.ok) {
      throw response;
    }

    return null;
  } catch (error) {
    console.error(error);
    return { messageType: "error", message: "Failed to remove studio doc. Please try again." };
  }
}

export async function setLocationWorkbook({
  builderId,
  locationId,
  body,
}: {
  builderId: string;
  locationId: string;
  body: LocationWorkbookSetSchema;
}): Promise<UserNotificationType | null> {
  try {
    const response = await postWithTimeout(getBuilderLocationWorkbookURL({ builderId, locationId }), body);

    if (!response.ok) {
      throw response;
    }

    return null;
  } catch (error) {
    console.error(error);
    return { messageType: "error", message: "Failed to save workbook. Please try again." };
  }
}

export async function removeLocationWorkbook({
  builderId,
  locationId,
}: {
  builderId: string;
  locationId: string;
}): Promise<UserNotificationType | null> {
  try {
    const response = await deleteWithTimeout(getBuilderLocationWorkbookURL({ builderId, locationId }));

    if (!response.ok) {
      throw response;
    }

    return null;
  } catch (error) {
    console.error(error);
    return { messageType: "error", message: "Failed to remove workbook. Please try again." };
  }
}

export async function postLocationWithMigratedChildren({
  builderId,
  location,
  locationIdsToMigrate,
}: {
  builderId: string;
  location: LocationUpdateFields & { locationLevel: LocationLevelType };
  locationIdsToMigrate: string[];
}): Promise<[ClientLocationCreationWithMigratedChildrenResponse, null] | [null, FetchErrorResponse]> {
  try {
    const res = await postWithTimeout(
      `${baseUrl(builderId)}/migrate-locations`,
      { location, locationIdsToMigrate },
      LOCATION_MIGRATION_TIMEOUT,
    );
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}
